import { PresenceDot } from "@/src/common/components/app-mock/primitives";

type HairStyle = "short" | "long" | "curly" | "bun" | "side" | "buzz" | "bob" | "afro";

interface AvatarArt {
	bg: string;
	skin: string;
	hair: string;
	shirt: string;
	style: HairStyle;
	glasses?: boolean;
	beard?: boolean;
}

const ART: ReadonlyArray<AvatarArt> = [
	{ bg: "#DDE7F6", skin: "#C68642", hair: "#2B1B12", shirt: "#3B82F6", style: "short" },
	{ bg: "#FCE7E1", skin: "#F1C27D", hair: "#4A2C17", shirt: "#10B981", style: "long" },
	{ bg: "#E6F4EA", skin: "#8D5524", hair: "#1A1A1A", shirt: "#F59E0B", style: "curly", glasses: true },
	{ bg: "#F3E8FF", skin: "#FFDBAC", hair: "#B5651D", shirt: "#6366F1", style: "bun" },
	{ bg: "#FFF4D6", skin: "#E0AC69", hair: "#3B2A20", shirt: "#EF4444", style: "side", beard: true },
	{ bg: "#E0F2FE", skin: "#A0673F", hair: "#0F0F0F", shirt: "#14B8A6", style: "buzz" },
	{ bg: "#FDE2E4", skin: "#F5D0B0", hair: "#C94F2E", shirt: "#8B5CF6", style: "bob", glasses: true },
	{ bg: "#E7E5E4", skin: "#7A4A2A", hair: "#141414", shirt: "#0EA5E9", style: "afro" },
];

const CAP = "M11.5 17a8.5 8.5 0 0 1 17 0c-2-3-5-4.5-8.5-4.5S13.5 14 11.5 17z";

function Hair({ art }: { art: AvatarArt }) {
	const fill = art.hair;
	switch (art.style) {
		case "short":
			return <path d={CAP} fill={fill} />;
		case "buzz":
			return <path d={CAP} fill={fill} opacity="0.8" />;
		case "long":
			return (
				<>
					<path d={CAP} fill={fill} />
					<path d="M11.5 17c0 4-.5 8-1.5 11h6V18zM28.5 17c0 4 .5 8 1.5 11h-6V18z" fill={fill} />
				</>
			);
		case "bun":
			return (
				<>
					<path d={CAP} fill={fill} />
					<circle cx="20" cy="8" r="3.2" fill={fill} />
				</>
			);
		case "curly":
			return (
				<>
					<path d="M10.5 18a9.5 9.5 0 0 1 19 0c-2-3-5.5-4.5-9.5-4.5S12.5 15 10.5 18z" fill={fill} />
					<circle cx="11" cy="17.5" r="2" fill={fill} />
					<circle cx="14" cy="11.5" r="2.2" fill={fill} />
					<circle cx="20" cy="8.5" r="2.4" fill={fill} />
					<circle cx="26" cy="11.5" r="2.2" fill={fill} />
					<circle cx="29" cy="17.5" r="2" fill={fill} />
				</>
			);
		case "side":
			return (
				<>
					<path d={CAP} fill={fill} />
					<path d="M11.5 17.5c3-4 9-6 17-4v3c-6-1-11 0-17 1z" fill={fill} />
				</>
			);
		case "bob":
			return (
				<>
					<path d={CAP} fill={fill} />
					<path d="M10.5 15.5c0 3 .5 6 .5 8.5h3.5v-7zM29.5 15.5c0 3-.5 6-.5 8.5h-3.5v-7z" fill={fill} />
				</>
			);
		case "afro":
			return <path d={CAP} fill={fill} />;
	}
}

function getAvatarArt(index: number): AvatarArt {
	const art = ART[((index % ART.length) + ART.length) % ART.length];
	return art ?? ART[0] ?? { bg: "#DDE7F6", skin: "#C68642", hair: "#2B1B12", shirt: "#3B82F6", style: "short" };
}

function AvatarArtwork({ index }: { index: number }) {
	const art = getAvatarArt(index);
	return (
		<svg viewBox="0 0 40 40" className="h-full w-full" aria-hidden="true">
			<rect width="40" height="40" fill={art.bg} />
			{art.style === "afro" ? <circle cx="20" cy="15" r="11.5" fill={art.hair} /> : null}
			<path d="M6 40c0-8 6-12 14-12s14 4 14 12z" fill={art.shirt} />
			<rect x="16.5" y="21" width="7" height="7" rx="2" fill={art.skin} />
			<circle cx="20" cy="16" r="8" fill={art.skin} />
			<Hair art={art} />
			{art.beard ? <path d="M13.5 18c0 5 3 8 6.5 8s6.5-3 6.5-8c-1.5 3-3.5 4-6.5 4s-5-1-6.5-4z" fill={art.hair} opacity="0.85" /> : null}
			{art.glasses ? (
				<g fill="none" stroke="#1f2937" strokeWidth="1">
					<circle cx="16.5" cy="17" r="3" />
					<circle cx="23.5" cy="17" r="3" />
					<path d="M19.5 17h1" />
				</g>
			) : null}
		</svg>
	);
}

interface MockAvatarPerson {
	name: string;
	initials: string;
	avatarIndex: number;
}

interface MockAvatarProps {
	person: MockAvatarPerson;
	size?: number;
	className?: string;
	presence?: "online" | "offline";
	fallback?: boolean;
	ring?: boolean;
}

export function MockAvatar({ person, size = 32, className = "", presence, fallback, ring }: MockAvatarProps) {
	const art = getAvatarArt(person.avatarIndex);
	return (
		<span
			className={`relative inline-flex shrink-0 overflow-visible ${className}`}
			style={{ width: size, height: size }}
			title={person.name}
			aria-hidden="true"
		>
			<span
				className={`flex h-full w-full items-center justify-center overflow-hidden rounded-full font-semibold ${ring ? "ring-2 ring-white" : ""}`}
				style={{ background: art.bg, color: "#1f2937", fontSize: Math.max(9, Math.round(size * 0.36)) }}
			>
				{fallback ? person.initials : <AvatarArtwork index={person.avatarIndex} />}
			</span>
			{presence ? <PresenceDot online={presence === "online"} size={Math.max(8, Math.round(size * 0.3))} /> : null}
		</span>
	);
}

interface AvatarStackProps {
	people: ReadonlyArray<MockAvatarPerson>;
	size?: number;
	max?: number;
	extra?: number;
}

export function MockAvatarStack({ people, size = 20, max = 3, extra }: AvatarStackProps) {
	const shown = people.slice(0, max);
	const remaining = extra ?? Math.max(0, people.length - max);
	return (
		<span className="inline-flex items-center" aria-hidden="true">
			{shown.map((person, index) => (
				<span key={person.name} className="rounded-full" style={{ marginLeft: index === 0 ? 0 : -size * 0.3 }}>
					<MockAvatar person={person} size={size} ring />
				</span>
			))}
			{remaining > 0 ? (
				<span
					className="inline-flex items-center justify-center rounded-full bg-neutral-200 font-semibold text-neutral-600 ring-2 ring-white"
					style={{ width: size, height: size, marginLeft: -size * 0.3, fontSize: Math.max(8, Math.round(size * 0.4)) }}
				>
					+{remaining}
				</span>
			) : null}
		</span>
	);
}
